import {
  Container,
  Title,
  Paper,
  TextInput,
  Button,
  Group,
  Stack,
  Text,
  Badge,
  ActionIcon,
  ThemeIcon,
  Loader,
  Notification,
  Box,
  Collapse,
  Center,
  Select,
  Table,
  Tabs,
  useComputedColorScheme,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  IconChartBar,
  IconAlertCircle,
  IconCalendar,
  IconKey,
  IconRefresh,
  IconUsers,
  IconChevronDown,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import analyzerService from "../../services/analyzerService";
import React, { useState, useEffect } from "react";

const Analyzer = () => {
  const computedColorScheme = useComputedColorScheme("light");
  const isDark = computedColorScheme === "dark";

  const [keitaroApiKey, setKeitaroApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().toDate(),
    dayjs().toDate(),
  ]);
  const [configOpened, setConfigOpened] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("all");
  const [breakdowns, setBreakdowns] = useState({});
  const [breakdownLoading, setBreakdownLoading] = useState({});
  const [breakdownCache, setBreakdownCache] = useState({});

  useEffect(() => {
    fetchConfig();
    const savedGroupId = localStorage.getItem("keitaro_selected_group_id");
    if (savedGroupId) setSelectedGroupId(savedGroupId);
  }, []);

  useEffect(() => {
    if (keitaroApiKey) fetchGroups();
  }, [keitaroApiKey]);

  const fetchConfig = async () => {
    try {
      const data = await analyzerService.getConfig();
      if (data.keitaroApiKey) setKeitaroApiKey(data.keitaroApiKey);
    } catch (err) {
      console.error("Error fetching config:", err);
    }
  };

  const fetchGroups = async () => {
    try {
      const groupsData = await analyzerService.getGroups();
      setGroups(groupsData || []);
    } catch (err) {
      console.error("Error fetching groups:", err);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await analyzerService.saveConfig(keitaroApiKey);
      setSuccess("API Key збережено");
      setTimeout(() => setSuccess(null), 3000);
      setConfigOpened(false);
      fetchGroups();
    } catch (err) {
      setError("Не вдалося зберегти налаштування");
    } finally {
      setSaving(false);
    }
  };

  const handleRunAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const from = dayjs(dateRange[0]).format("YYYY-MM-DD");
      const to = dateRange[1]
        ? dayjs(dateRange[1]).format("YYYY-MM-DD")
        : dayjs(dateRange[0]).format("YYYY-MM-DD");
      const data = await analyzerService.runAnalysis(from, to, selectedGroupId);
      setAnalysisData(data.analysis);
      setBreakdownCache({});
    } catch (err) {
      setError(err.response?.data?.message || "Не вдалося отримати аналіз");
    } finally {
      setLoading(false);
    }
  };

  const fetchBreakdown = async (campaignId, dimension, force = false) => {
    const from = dayjs(dateRange[0]).format("YYYY-MM-DD");
    const to = dateRange[1]
      ? dayjs(dateRange[1]).format("YYYY-MM-DD")
      : dayjs(dateRange[0]).format("YYYY-MM-DD");
    const dateKey = `${from}_${to}`;

    if (
      !force &&
      breakdownCache[campaignId]?.[dimension]?.dateKey === dateKey
    ) {
      setBreakdowns((prev) => ({
        ...prev,
        [campaignId]: {
          ...prev[campaignId],
          [dimension]: breakdownCache[campaignId][dimension].data,
        },
      }));
      return;
    }

    setBreakdownLoading((prev) => ({ ...prev, [campaignId]: true }));
    try {
      const data = await analyzerService.getCampaignBreakdown(
        campaignId,
        from,
        to,
        dimension,
      );
      setBreakdownCache((prev) => ({
        ...prev,
        [campaignId]: { ...prev[campaignId], [dimension]: { dateKey, data } },
      }));
      setBreakdowns((prev) => ({
        ...prev,
        [campaignId]: { ...prev[campaignId], [dimension]: data },
      }));
    } catch (err) {
      console.error("Error fetching breakdown:", err);
    } finally {
      setBreakdownLoading((prev) => ({ ...prev, [campaignId]: false }));
    }
  };

  const handleGroupChange = (value) => {
    setSelectedGroupId(value);
    localStorage.setItem("keitaro_selected_group_id", value);
  };

  const getRoiColor = (roi) => {
    const r = parseFloat(roi);
    if (r > 50) return "teal";
    if (r > 20) return "blue";
    if (r > 0) return "yellow";
    return "red";
  };

  const stats = analysisData
    ? {
        count: analysisData.length,
        clicks: analysisData.reduce(
          (acc, curr) => acc + (parseInt(curr.clicks) || 0),
          0,
        ),
        revenue: analysisData.reduce(
          (acc, curr) => acc + (parseFloat(curr.revenue) || 0),
          0,
        ),
        cost: analysisData.reduce(
          (acc, curr) => acc + (parseFloat(curr.cost) || 0),
          0,
        ),
        get roi() {
          return this.cost > 0
            ? ((this.revenue - this.cost) / this.cost) * 100
            : this.revenue > 0
              ? 100
              : 0;
        },
      }
    : null;

  return (
    <Container size="xl" p={0}>
      <Stack gap="lg">
        {/* Header Section */}
        <Group justify="space-between" align="center">
          <Box>
            <Title
              order={2}
              fw={600}
            >
              Keitaro Analytics
            </Title>
            <Text c="dimmed" size="xs" fw={500}>
              Професійний аналіз та оптимізація
            </Text>
          </Box>

          <Group gap="xs">
            {groups.length > 0 && (
              <Select
                placeholder="Всі групи"
                data={[
                  { value: "all", label: "Всі групи" },
                  ...groups.map((g) => ({
                    value: g.id.toString(),
                    label: g.name,
                  })),
                ]}
                value={selectedGroupId}
                onChange={handleGroupChange}
                size="sm"
                leftSection={<IconUsers size={16} />}
                style={{ width: 180 }}
              />
            )}
            <DatePickerInput
              type="range"
              placeholder="Період"
              value={dateRange}
              onChange={setDateRange}
              size="sm"
              allowSingleDateInRange
              leftSection={<IconCalendar size={16} />}
              style={{ width: 230 }}
            />
            <ActionIcon
              variant="light"
              size="lg"
              radius="md"
              onClick={() => setConfigOpened(!configOpened)}
            >
              <IconKey size={20} />
            </ActionIcon>
            <Button
              onClick={handleRunAnalysis}
              loading={loading}
              disabled={!keitaroApiKey || !dateRange[0]}
              radius="md"
              leftSection={<IconRefresh size={18} />}
            >
              Аналізувати
            </Button>
          </Group>
        </Group>

        <Collapse in={configOpened}>
          <Paper
            withBorder
            p="sm"
            radius="md"
            bg={
              isDark
                ? "var(--mantine-color-dark-8)"
                : "var(--mantine-color-gray-0)"
            }
          >
            <Group align="flex-end" gap="sm">
              <TextInput
                label="Keitaro API Key"
                placeholder="API Key"
                value={keitaroApiKey}
                onChange={(e) => setKeitaroApiKey(e.currentTarget.value)}
                type="password"
                size="xs"
                style={{ flex: 1 }}
              />
              <Button onClick={handleSaveConfig} loading={saving} size="xs">
                Зберегти
              </Button>
            </Group>
          </Paper>
        </Collapse>

        {error && (
          <Notification
            color="red"
            title="Помилка"
            onClose={() => setError(null)}
          >
            {error}
          </Notification>
        )}
        {success && (
          <Notification
            color="teal"
            title="Успіх"
            onClose={() => setSuccess(null)}
          >
            {success}
          </Notification>
        )}

        {stats && (
          <Paper
            withBorder
            px="md"
            py="xs"
            radius="md"
            bg={isDark ? "rgba(36, 36, 36, 0.4)" : "rgba(255, 255, 255, 0.8)"}
            style={{ backdropFilter: "blur(5px)" }}
          >
            <Group justify="space-between">
              <Group gap="xl">
                <Box>
                  <Text size="xs" c="dimmed" fw={700}>
                    КАМПАНІЇ
                  </Text>
                  <Text fw={800} size="sm">
                    {stats.count}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed" fw={700}>
                    КЛІКИ
                  </Text>
                  <Text fw={800} size="sm">
                    {stats.clicks.toLocaleString()}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed" fw={700}>
                    ВИТРАТИ
                  </Text>
                  <Text fw={800} size="sm" c="red">
                    ${stats.cost.toFixed(2)}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed" fw={700}>
                    ПРИБУТОК
                  </Text>
                  <Text
                    fw={1000}
                    size="sm"
                    c={stats.revenue - stats.cost >= 0 ? "green" : "red"}
                  >
                    ${(stats.revenue - stats.cost).toFixed(2)}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed" fw={700}>
                    ROI
                  </Text>
                  <Text fw={800} size="sm" c={getRoiColor(stats.roi)}>
                    {stats.roi.toFixed(1)}%
                  </Text>
                </Box>
              </Group>
            </Group>
          </Paper>
        )}

        {loading ? (
          <Center py={100}>
            <Stack align="center" gap="sm">
              <Loader color="blue" size="lg" type="bars" />
              <Text c="dimmed" size="xs" fw={600} tt="uppercase">
                Синхронізація...
              </Text>
            </Stack>
          </Center>
        ) : analysisData ? (
          <Paper withBorder radius="sm" style={{ overflow: "hidden" }}>
            <Table
              verticalSpacing="xs"
              highlightOnHover
              withColumnBorders
              fontSize="xs"
            >
              <Table.Thead
                bg={
                  isDark
                    ? "var(--mantine-color-dark-6)"
                    : "var(--mantine-color-gray-1)"
                }
              >
                <Table.Tr>
                  <Table.Th style={{ width: 40 }}></Table.Th>
                  <Table.Th>Назва</Table.Th>
                  <Table.Th ta="right">Кліки</Table.Th>
                  <Table.Th ta="right">Дохід</Table.Th>
                  <Table.Th ta="right">Витрати</Table.Th>
                  <Table.Th ta="right">Прибуток</Table.Th>
                  <Table.Th ta="right">ROI</Table.Th>
                  <Table.Th ta="right">CPL</Table.Th>
                  <Table.Th ta="center">Tips</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {analysisData.map((campaign) => (
                  <CampaignRow
                    key={campaign.id}
                    campaign={campaign}
                    isDark={isDark}
                    getRoiColor={getRoiColor}
                    fetchBreakdown={fetchBreakdown}
                    breakdowns={breakdowns}
                    breakdownLoading={breakdownLoading}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        ) : (
          <Paper
            p={60}
            withBorder
            radius="md"
            style={{ borderStyle: "dashed", textAlign: "center" }}
            bg={
              isDark
                ? "var(--mantine-color-dark-8)"
                : "var(--mantine-color-gray-0)"
            }
          >
            <Stack align="center" gap="md">
              <ThemeIcon size={60} radius="xl" variant="light" color="gray">
                <IconChartBar size={34} />
              </ThemeIcon>
              <Text fw={700} size="lg">
                Немає даних
              </Text>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Container>
  );
};

const CampaignRow = ({
  campaign,
  isDark,
  getRoiColor,
  fetchBreakdown,
  breakdowns,
  breakdownLoading,
}) => {
  const [opened, setOpened] = useState(false);

  return (
    <>
      <Table.Tr
        onClick={() => setOpened(!opened)}
        style={{ cursor: "pointer" }}
        bg={
          opened
            ? isDark
              ? "var(--mantine-color-dark-8)"
              : "var(--mantine-color-blue-0)"
            : undefined
        }
      >
        <Table.Td ta="center">
          <IconChevronDown
            size={14}
            style={{
              transform: opened ? "rotate(180deg)" : "none",
              transition: "transform 0.2s",
            }}
          />
        </Table.Td>
        <Table.Td fw={700}>{campaign.name}</Table.Td>
        <Table.Td ta="right">{campaign.clicks}</Table.Td>
        <Table.Td ta="right" c="green" fw={600}>
          ${campaign.revenue}
        </Table.Td>
        <Table.Td ta="right" c="red">
          ${campaign.cost}
        </Table.Td>
        <Table.Td
          ta="right"
          fw={700}
          c={parseFloat(campaign.profit) >= 0 ? "green" : "red"}
        >
          ${campaign.profit}
        </Table.Td>
        <Table.Td ta="right">
          <Badge size="xs" color={getRoiColor(campaign.roi)} radius="xs">
            {campaign.roi}%
          </Badge>
        </Table.Td>
        <Table.Td ta="right" fw={600}>
          ${campaign.cpl}
        </Table.Td>
        <Table.Td ta="center">
          {campaign.tips.length > 0 && (
            <ThemeIcon size="sm" variant="light" color="orange">
              <IconAlertCircle size={12} />
            </ThemeIcon>
          )}
        </Table.Td>
      </Table.Tr>
      <Table.Tr>
        <Table.Td colSpan={9} p={0}>
          <Collapse in={opened}>
            <Box
              p="md"
              bg={
                isDark
                  ? "var(--mantine-color-dark-9)"
                  : "var(--mantine-color-gray-0)"
              }
            >
              <Stack gap="md">
                <Group
                  gap="xl"
                  px="sm"
                  py="xs"
                  style={{
                    borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                  }}
                >
                  <Box>
                    <Text size="10px" c="dimmed" fw={700}>
                      CPC / EPC
                    </Text>
                    <Text size="xs" fw={700}>
                      <Text span c="red">
                        ${campaign.cpc}
                      </Text>{" "}
                      /{" "}
                      <Text span c="green">
                        ${campaign.epc}
                      </Text>
                    </Text>
                  </Box>
                  <Box>
                    <Text size="10px" c="dimmed" fw={700}>
                      LP CTR / CR
                    </Text>
                    <Text size="xs" fw={700}>
                      {campaign.lp_ctr}% / {campaign.cr}%
                    </Text>
                  </Box>
                  <Box>
                    <Text size="10px" c="dimmed" fw={700}>
                      ПРОГНОЗ ROI (AR: {campaign.historicalAr}%)
                    </Text>
                    <Text
                      size="xs"
                      fw={700}
                      c={getRoiColor(campaign.predictedRoi)}
                    >
                      {campaign.predictedRoi}%
                    </Text>
                  </Box>
                  <Box>
                    <Text size="10px" c="dimmed" fw={700}>
                      CONVERSIONS (A/H/R)
                    </Text>
                    <Group gap={4}>
                      <Text size="xs" fw={700} c="green">
                        {campaign.approved}
                      </Text>
                      <Text size="xs" fw={700} c="orange">
                        /{campaign.hold}
                      </Text>
                      <Text size="xs" fw={700} c="red">
                        /{campaign.rejected}
                      </Text>
                    </Group>
                  </Box>
                </Group>
                {campaign.tips.length > 0 && (
                  <Group gap="xs" px="sm">
                    {campaign.tips.map((tip, idx) => (
                      <Badge
                        key={idx}
                        variant="dot"
                        color="brand"
                        size="xs"
                        styles={{ label: { textTransform: "none" } }}
                      >
                        {tip}
                      </Badge>
                    ))}
                  </Group>
                )}
                <Box mt="xs">
                  <Tabs
                    defaultValue="sub_id_1"
                    size="xs"
                    variant="outline"
                    onChange={(val) => fetchBreakdown(campaign.id, val)}
                  >
                    <Tabs.List mb="xs">
                      <Tabs.Tab value="sub_id_1">Плейсменти</Tabs.Tab>
                      <Tabs.Tab value="sub_id_3">Адсети</Tabs.Tab>
                      <Tabs.Tab value="sub_id_4">Оголошення</Tabs.Tab>
                      <Tabs.Tab value="creative_id">Креативи</Tabs.Tab>
                      <Box style={{ flex: 1 }} />
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        onClick={() =>
                          fetchBreakdown(campaign.id, "sub_id_1", true)
                        }
                        loading={breakdownLoading[campaign.id]}
                      >
                        <IconRefresh size={12} />
                      </ActionIcon>
                    </Tabs.List>
                    {["sub_id_1", "sub_id_3", "sub_id_4", "creative_id"].map(
                      (dim) => (
                        <Tabs.Panel key={dim} value={dim}>
                          {breakdowns[campaign.id]?.[dim] ? (
                            <Table
                              verticalSpacing="xs"
                              highlightOnHover
                              fontSize="xs"
                              withColumnBorders
                              dense
                            >
                              <Table.Thead
                                bg={
                                  isDark
                                    ? "var(--mantine-color-dark-8)"
                                    : "var(--mantine-color-gray-1)"
                                }
                              >
                                <Table.Tr>
                                  <Table.Th>Об'єкт</Table.Th>
                                  <Table.Th ta="right">Кліки</Table.Th>
                                  <Table.Th ta="right">CPC/EPC</Table.Th>
                                  <Table.Th ta="right">
                                    Витрати / Прибуток
                                  </Table.Th>
                                  <Table.Th ta="right">ROI</Table.Th>
                                  <Table.Th>Порада</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {breakdowns[campaign.id][dim].map(
                                  (row, idx) => (
                                    <Table.Tr key={idx}>
                                      <Table.Td fw={500}>{row.label}</Table.Td>
                                      <Table.Td ta="right">
                                        {row.clicks}
                                      </Table.Td>
                                      <Table.Td ta="right">
                                        <Text size="xs" span c="red">
                                          ${row.cpc}
                                        </Text>{" "}
                                        /{" "}
                                        <Text size="xs" span c="green">
                                          ${row.epc}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td ta="right">
                                        <Text size="xs" span c="red">
                                          -${row.cost.toFixed(1)}
                                        </Text>{" "}
                                        /{" "}
                                        <Text
                                          size="xs"
                                          span
                                          fw={700}
                                          c={row.profit >= 0 ? "green" : "red"}
                                        >
                                          ${row.profit.toFixed(1)}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td ta="right">
                                        <Badge
                                          size="xs"
                                          radius="xs"
                                          color={getRoiColor(row.roi)}
                                        >
                                          {row.roi.toFixed(0)}%
                                        </Badge>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text
                                          size="10px"
                                          fw={700}
                                          c={row.tipColor}
                                        >
                                          {row.tip}
                                        </Text>
                                      </Table.Td>
                                    </Table.Tr>
                                  ),
                                )}
                              </Table.Tbody>
                            </Table>
                          ) : (
                            <Paper
                              p="xl"
                              withBorder
                              radius="xs"
                              bg={
                                isDark
                                  ? "var(--mantine-color-dark-8)"
                                  : "var(--mantine-color-gray-1)"
                              }
                              mt="xs"
                            >
                              <Center>
                                {breakdownLoading[campaign.id] ? (
                                  <Loader size="xs" />
                                ) : (
                                  <Text size="xs" c="dimmed">
                                    Виберіть вкладку для завантаження
                                  </Text>
                                )}
                              </Center>
                            </Paper>
                          )}
                        </Tabs.Panel>
                      ),
                    )}
                  </Tabs>
                </Box>
              </Stack>
            </Box>
          </Collapse>
        </Table.Td>
      </Table.Tr>
    </>
  );
};

export default Analyzer;
